import { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box } from "@mui/material";
import Product from "../Product/Product";
import ProductCategory from "../Product/ProductCategory";
import ProductBrand from "../Product/ProductBrand";
import Category from "../Category/Category";
import Brand from "../Brand/Brand";
import CategoryManager from "../Category/CategoryManager";
import BrandManager from "../Brand/BrandManager";
import HiperSyncProducts from "../Hiper/HiperSyncProducts";
import UserManager from "../User/UserManager";
import RoleManager from "../Role/RoleManager";
import Promotion from "../Promotion/Promotion";
import { PromotionView, PromotionDetails } from "../Promotion";
import { closeUserManager } from "../../store/slices/userManagerSlice";
import { closeRoleManager } from "../../store/slices/roleManagerSlice";
import { closePromotionManager } from "../../store/slices/promotionSlice";
import { closeCategoryManager } from "../../store/slices/categoryManagerSlice";
import { closeBrandManager } from "../../store/slices/brandManagerSlice";
import { closeHiperSync } from "../../store/slices/hiperSyncSlice";
import { closeProductManager } from "../../store/slices/productManagerSlice";
import { closeLandpageManager } from "../../store/slices/landpageManagerSlice";
import ProductManager from "../Product/ProductManager";
import LandpageManager from "../Landpage/LandpageManager";
import LandpageView from "../Landpage/LandpageView";

/**
 * Componente da área de trabalho principal.
 * Carrega e exibe produtos da API com layout responsivo otimizado.
 * Mobile: 1 card por linha, Tablet: 2 cards por linha, Desktop: 4 cards por linha.
 * Suporta navegação entre favoritos, categorias, produtos por categoria e produtos gerais.
 * Favoritos e categorias são mutuamente exclusivos para evitar conflitos de interface.
 *
 * @component
 * @returns {JSX.Element} Área de trabalho com grid responsivo de produtos, categorias ou produtos de categoria específica
 */
export default function Workspace() {
  const dispatch = useDispatch();
  const { showCategories, selectedCategory, showBrands, selectedBrand, showFavorites, showPromotions, selectedPromotion } = useSelector((state) => state.products);
  const { isOpen: userManagerOpen } = useSelector((state) => state.userManager);
  const { isOpen: roleManagerOpen } = useSelector((state) => state.roleManager);
  const { isOpen: promotionOpen } = useSelector((state) => state.promotion);
  const { isOpen: categoryManagerOpen } = useSelector((state) => state.categoryManager);
  const { isOpen: brandManagerOpen } = useSelector((state) => state.brandManager);
  const { isOpen: hiperSyncOpen } = useSelector((state) => state.hiperSync);
  const { isOpen: productManagerOpen } = useSelector((state) => state.productManager);
  const { isOpen: landpageManagerOpen, showLandpage, activeLandpageConfig } = useSelector((state) => state.landpageManager);

  /** Qualquer navegação de produto/categoria/promoção oculta a landpage */
  const hasOtherView = showCategories || selectedCategory || showBrands || selectedBrand || showFavorites || showPromotions || selectedPromotion;

  /**
   * Fecha modais quando outra navegação é ativada.
   */
  useEffect(() => {
    if (userManagerOpen && hasOtherView) dispatch(closeUserManager());
    if (roleManagerOpen && hasOtherView) dispatch(closeRoleManager());
    if (promotionOpen && hasOtherView) dispatch(closePromotionManager());
    if (categoryManagerOpen && hasOtherView) dispatch(closeCategoryManager());
    if (brandManagerOpen && hasOtherView) dispatch(closeBrandManager());
    if (hiperSyncOpen && hasOtherView) dispatch(closeHiperSync());
    if (productManagerOpen && hasOtherView) dispatch(closeProductManager());
    if (landpageManagerOpen && hasOtherView) dispatch(closeLandpageManager());
  }, [hasOtherView, userManagerOpen, roleManagerOpen, promotionOpen, categoryManagerOpen, brandManagerOpen, hiperSyncOpen, productManagerOpen, landpageManagerOpen, dispatch]);

  /** Exibe landpage quando não há outra view ativa e showLandpage está ligado */
  const showingLandpage = showLandpage && !hasOtherView && activeLandpageConfig;

  return (
    <>
      {/* Landpage ativa — exibida na página inicial */}
      {showingLandpage && <LandpageView config={activeLandpageConfig} />}

      {/* Se uma promoção específica estiver selecionada, renderiza PromotionDetails */}
      {selectedPromotion && !selectedCategory && !selectedBrand && <PromotionDetails />}

      {/* Se showPromotions estiver ativo, renderiza PromotionView */}
      {showPromotions && !selectedPromotion && !selectedCategory && !selectedBrand && <PromotionView />}

      {/* Se uma categoria específica estiver selecionada, renderiza ProductCategory */}
      {selectedCategory && <ProductCategory />}

      {/* Se uma marca específica estiver selecionada, renderiza ProductBrand */}
      {selectedBrand && !selectedCategory && <ProductBrand />}

      {/* Se showCategories estiver ativo, renderiza o componente Category */}
      {showCategories && !selectedCategory && !selectedBrand && !showPromotions && <Category />}

      {/* Se showBrands estiver ativo, renderiza o componente Brand */}
      {showBrands && !selectedCategory && !selectedBrand && !showPromotions && <Brand />}

      {/* Produtos gerais — exibidos quando não há outra view ativa e não há landpage */}
      {!showingLandpage && !showCategories && !selectedCategory && !showBrands && !selectedBrand && !showPromotions && !selectedPromotion && <Product />}

      {/* Modal de gerenciamento de usuários */}
      <UserManager
        open={userManagerOpen}
        onClose={() => dispatch(closeUserManager())}
      />

      {/* Modal de gerenciamento de roles */}
      <RoleManager
        open={roleManagerOpen}
        onClose={() => dispatch(closeRoleManager())}
      />

      {/* Modal de gerenciamento de promoções */}
      <Promotion
        open={promotionOpen}
        onClose={() => dispatch(closePromotionManager())}
      />

      {/* Modal de gerenciamento de categorias */}
      <CategoryManager
        open={categoryManagerOpen}
        onClose={() => dispatch(closeCategoryManager())}
      />

      {/* Modal de gerenciamento de marcas */}
      <BrandManager
        open={brandManagerOpen}
        onClose={() => dispatch(closeBrandManager())}
      />

      {/* Modal de sincronização Hiper */}
      <HiperSyncProducts
        open={hiperSyncOpen}
        onClose={() => dispatch(closeHiperSync())}
      />

      {/* Modal de gerenciamento de produtos */}
      <ProductManager
        open={productManagerOpen}
        onClose={() => dispatch(closeProductManager())}
      />

      {/* Modal de gerenciamento de landpage */}
      <LandpageManager
        open={landpageManagerOpen}
        onClose={() => dispatch(closeLandpageManager())}
      />
    </>
  );
}
